import { reactive } from 'vue'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import api, { getAccessToken } from '@/api/client'
import { uploadErrorMessage } from '@/services/uploadErrors'

export type UploadStage =
  | 'sending'
  | 'preparing'
  | 'uploading'
  | 'scanning'
  | 'importing'
  | 'converting'
  | 'compressing'
  | 'optimizing'
  | 'saving'
  | 'finalizing'
  | 'done'
  | 'failed'
  | 'canceled'

export interface ActiveUpload {
  id: string
  kind: string
  stage: UploadStage
  status: string
  networkProgress: number | null
  processingProgress: number | null
  error: string
}

interface UploadSessionPayload {
  id: string
  kind: string
  stage: UploadStage
  status: string
  error?: string
  progress?: number
}

const activeUploads = reactive<ActiveUpload[]>([])
const requestControllers = new Map<string, AbortController>()
const observerControllers = new Map<string, AbortController>()
let successSound: HTMLAudioElement | null = null
let failedSound: HTMLAudioElement | null = null
const playingUploadSounds = new Set<HTMLAudioElement>()

function uploadSound(success: boolean): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null
  const audio = success
    ? (successSound ||= new Audio('/media/site/success.wav'))
    : (failedSound ||= new Audio('/media/site/failed.wav'))
  audio.preload = 'auto'
  return audio
}

export interface TrackedUploadOptions {
  playSound?: boolean
  maxAttempts?: number
  idempotencyKey?: string
}

export function primeUploadResultSounds(): void {
  uploadSound(true)?.load()
  uploadSound(false)?.load()
}

export function playUploadResultSound(success: boolean): void {
  const template = uploadSound(success)
  if (!template) return
  const audio = template.cloneNode(true) as HTMLAudioElement
  audio.preload = 'auto'
  const release = () => playingUploadSounds.delete(audio)
  playingUploadSounds.add(audio)
  audio.addEventListener('ended', release, { once: true })
  audio.addEventListener('error', release, { once: true })
  audio.currentTime = 0
  void audio.play().catch(() => {
    release()
    /* Browser autoplay policy may block status sounds. */
  })
}

function uploadById(id: string): ActiveUpload | undefined {
  return activeUploads.find(upload => upload.id === id)
}

function applySession(payload: UploadSessionPayload): void {
  const upload = uploadById(payload.id)
  if (!upload) return
  upload.stage = payload.stage
  upload.status = payload.status
  upload.error = payload.error || ''
  upload.processingProgress = Number.isFinite(payload.progress) && payload.progress! > 0 && payload.progress! < 100
    ? Math.round(payload.progress!)
    : null
  if (payload.stage !== 'sending') upload.networkProgress = null
}

function finishLater(id: string, delay = 6_000): void {
  window.setTimeout(() => {
    const index = activeUploads.findIndex(upload => upload.id === id)
    if (index >= 0) activeUploads.splice(index, 1)
    requestControllers.delete(id)
    observerControllers.get(id)?.abort()
    observerControllers.delete(id)
  }, delay)
}

function canceled(error: any, signal?: AbortSignal): boolean {
  return Boolean(signal?.aborted
    || error?.code === 'ERR_CANCELED'
    || error?.name === 'CanceledError'
    || error?.name === 'AbortError')
}

function retryable(error: any): boolean {
  const status = Number(error?.response?.status || 0)
  return status === 0 || status === 408 || status === 409 || status === 425 || status === 429 || status >= 500
}

function retryAfterMs(error: any): number {
  const raw = String(error?.response?.headers?.['retry-after'] || '').trim()
  if (!raw) return 0
  const seconds = Number.parseFloat(raw)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1_000)
  const at = Date.parse(raw)
  return Number.isFinite(at) ? Math.max(0, at - Date.now()) : 0
}

function waitForRetry(attempt: number, error: any, signal: AbortSignal): Promise<void> {
  const exponential = Math.min(5_000, 350 * (2 ** Math.max(0, attempt - 1)))
  const delay = Math.max(retryAfterMs(error), Math.floor(Math.random() * exponential))
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, delay)
    const onAbort = () => {
      window.clearTimeout(timer)
      reject(new DOMException('Upload canceled', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

async function createUploadSession(kind: string, signal?: AbortSignal): Promise<UploadSessionPayload> {
  let lastError: unknown
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const { data } = await api.post<UploadSessionPayload>('/uploads/sessions', { kind }, { signal })
      return data
    } catch (error: any) {
      lastError = error
      if (canceled(error, signal) || attempt === 3 || !retryable(error)) throw error
      const controller = new AbortController()
      if (signal) {
        if (signal.aborted) controller.abort()
        else signal.addEventListener('abort', () => controller.abort(), { once: true })
      }
      await waitForRetry(attempt, error, controller.signal)
    }
  }
  throw lastError
}

async function pollUpload(id: string, observerSignal: AbortSignal): Promise<void> {
  while (!observerSignal.aborted) {
    try {
      const { data } = await api.get<UploadSessionPayload>(`/uploads/sessions/${id}`)
      applySession(data)
      if (['done', 'failed', 'canceled'].includes(data.stage)) return
    } catch {
      // A reconnect or token refresh can transiently interrupt polling.
    }
    await new Promise(resolve => window.setTimeout(resolve, 500))
  }
}

async function observeUpload(id: string, controller: AbortController): Promise<void> {
  const token = getAccessToken()
  try {
    const base = String(api.defaults.baseURL || '/api').replace(/\/$/, '')
    const response = await fetch(`${base}/uploads/sessions/${encodeURIComponent(id)}/events`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
      signal: controller.signal,
    })
    if (!response.ok || !response.body) throw new Error('SSE unavailable')
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (!controller.signal.aborted) {
      const { done, value } = await reader.read()
      if (done) return
      buffer += decoder.decode(value, { stream: true })
      const events = buffer.split(/\r?\n\r?\n/)
      buffer = events.pop() || ''
      for (const event of events) {
        const line = event.split(/\r?\n/).find(item => item.startsWith('data:'))
        if (!line) continue
        applySession(JSON.parse(line.slice(5).trim()) as UploadSessionPayload)
      }
    }
  } catch {
    if (!controller.signal.aborted) await pollUpload(id, controller.signal)
  }
}

export async function trackedUpload<T = unknown>(
  kind: string,
  config: AxiosRequestConfig,
  options: TrackedUploadOptions = {},
): Promise<AxiosResponse<T>> {
  const sourceSignal = config.signal as AbortSignal | undefined
  let session: UploadSessionPayload
  try {
    session = await createUploadSession(kind, sourceSignal)
  } catch (error) {
    if (!canceled(error, sourceSignal) && options.playSound !== false) playUploadResultSound(false)
    throw error
  }
  const requestController = new AbortController()
  requestControllers.set(session.id, requestController)
  activeUploads.push({
    id: session.id,
    kind,
    stage: 'sending',
    status: 'running',
    networkProgress: 0,
    processingProgress: null,
    error: '',
  })
  if (sourceSignal) {
    if (sourceSignal.aborted) requestController.abort()
    else sourceSignal.addEventListener('abort', () => requestController.abort(), { once: true })
  }

  const startObserver = () => {
    observerControllers.get(session.id)?.abort()
    const controller = new AbortController()
    observerControllers.set(session.id, controller)
    void observeUpload(session.id, controller)
  }

  const maxAttempts = Math.max(1, Math.min(6, Math.trunc(options.maxAttempts || 1)))
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const upload = uploadById(session.id)
    if (upload) {
      upload.stage = 'sending'
      upload.status = attempt === 1 ? 'running' : `retrying (${attempt}/${maxAttempts})`
      upload.networkProgress = 0
      upload.processingProgress = null
      upload.error = ''
    }
    startObserver()
    try {
      const response = await api.request<T>({
        ...config,
        timeout: 0,
        signal: requestController.signal,
        headers: {
          ...config.headers,
          'X-Upload-Session': session.id,
          ...(options.idempotencyKey ? { 'X-Upload-Request-ID': options.idempotencyKey } : {}),
        },
        onUploadProgress: event => {
          const active = uploadById(session.id)
          if (active && event.total) {
            active.stage = 'sending'
            active.networkProgress = Math.min(100, Math.round((event.loaded * 100) / event.total))
            active.processingProgress = null
          }
          config.onUploadProgress?.(event)
        },
      })
      const active = uploadById(session.id)
      if (active) {
        active.networkProgress = null
        active.processingProgress = null
        active.stage = 'done'
        active.status = 'complete'
      }
      if (options.playSound !== false) playUploadResultSound(true)
      finishLater(session.id, 6_000)
      return response
    } catch (error: any) {
      const isCanceled = canceled(error, requestController.signal)
      const canRetry = !isCanceled && attempt < maxAttempts && retryable(error)
      observerControllers.get(session.id)?.abort()
      if (canRetry) {
        const active = uploadById(session.id)
        if (active) {
          active.stage = 'preparing'
          active.status = `retrying (${attempt + 1}/${maxAttempts})`
          active.networkProgress = null
          active.processingProgress = null
          active.error = uploadErrorMessage(error, 'Temporary upload error')
        }
        try {
          await waitForRetry(attempt, error, requestController.signal)
        } catch (retryError) {
          const canceledUpload = uploadById(session.id)
          if (canceledUpload) {
            canceledUpload.stage = 'canceled'
            canceledUpload.status = 'canceled'
            canceledUpload.networkProgress = null
            canceledUpload.processingProgress = null
            canceledUpload.error = 'Upload canceled'
          }
          finishLater(session.id, 6_000)
          throw retryError
        }
        continue
      }
      const active = uploadById(session.id)
      if (active) {
        active.networkProgress = null
        active.processingProgress = null
        active.stage = isCanceled ? 'canceled' : 'failed'
        active.status = active.stage
        active.error = isCanceled ? 'Upload canceled' : uploadErrorMessage(error, 'Upload failed')
      }
      if (!isCanceled && options.playSound !== false) playUploadResultSound(false)
      finishLater(session.id, active?.stage === 'failed' ? 12_000 : 6_000)
      throw error
    }
  }
  throw new Error('Upload failed')
}

export async function cancelTrackedUpload(id: string): Promise<void> {
  requestControllers.get(id)?.abort()
  try { await api.delete(`/uploads/sessions/${encodeURIComponent(id)}`) } catch { /* request may already be gone */ }
  const upload = uploadById(id)
  if (upload) {
    upload.stage = 'canceled'
    upload.status = 'canceled'
    upload.networkProgress = null
    upload.processingProgress = null
  }
}

export function useTrackedUploads() {
  return { activeUploads, cancelTrackedUpload }
}
